import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
const PROPERTY_LAT = -37.8174906;
const PROPERTY_LNG = 145.046499;
const PROPERTY_LABEL = '1 Harcourt Street, Hawthorn East VIC 3123';
type Amenity = {
  name: string;
  lat: number;
  lng: number;
  distance: string | null;
};
const AMENITIES: Amenity[] = [{
  name: 'Auburn Village',
  lat: -37.8240549,
  lng: 145.0456769,
  distance: '650m'
}, {
  name: 'Auburn Station',
  lat: -37.82249,
  lng: 145.0459,
  distance: '750m'
}, {
  name: 'Rathmines Reserve',
  lat: -37.8198548,
  lng: 145.05416,
  distance: '900m'
}, {
  name: 'Camberwell Junction',
  lat: -37.8291302,
  lng: 145.0566936,
  distance: '1.4km'
}, {
  name: 'Glenferrie Road',
  lat: -37.8274015,
  lng: 145.0344618,
  distance: '1.5km'
}, {
  name: 'Scotch College',
  lat: -37.834218,
  lng: 145.0291482,
  distance: '1.5km'
}];
export function HarcourtStreetMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    // Inject Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    // Wait for CSS to load
    setTimeout(() => {
      if (!mapRef.current) return;
      // On touch devices a one-finger drag would hijack page scrolling, so
      // panning is disabled there — the pins, pills and zoom controls still
      // carry the location context.
      const isTouch = L.Browser.mobile;
      const map = L.map(mapRef.current, {
        center: [PROPERTY_LAT, PROPERTY_LNG],
        zoom: isTouch ? 13 : 14,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false,
        dragging: !isTouch,
        tap: false
      });
      // CartoDB Positron — clean minimal white tiles
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        subdomains: 'abcd'
      }).addTo(map);
      // Add zoom control to bottom right
      L.control.zoom({
        position: 'bottomright'
      }).addTo(map);
      // Custom property marker
      const propertyIcon = L.divIcon({
        className: 'property-marker',
        html: `
          <div style="
            width: 16px;
            height: 16px;
            background: #000;
            border-radius: 50%;
            border: 3px solid #fff;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          "></div>
        `,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      L.marker([PROPERTY_LAT, PROPERTY_LNG], {
        icon: propertyIcon
      }).addTo(map).bindPopup(`<div style="font-family: 'Playfair Display', serif; text-align: center; padding: 4px 0;">
            <strong style="font-size: 13px; letter-spacing: 0.05em;">${PROPERTY_LABEL}</strong>
          </div>`, {
        closeButton: false,
        className: 'property-popup'
      });
      // 15-minute walking radius (~1200m at 80m/min)
      L.circle([PROPERTY_LAT, PROPERTY_LNG], {
        radius: 1200,
        color: '#8FC6EC',
        weight: 1.5,
        opacity: 0.9,
        fillColor: '#C6E4FA',
        fillOpacity: 0.3,
        dashArray: '6, 6',
        interactive: false
      }).addTo(map);
      // Walking radius label
      const walkLabelIcon = L.divIcon({
        className: 'walk-label',
        html: `<span style="
          font-family: 'Montserrat', sans-serif;
          font-size: 9px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(0,0,0,0.3);
          white-space: nowrap;
          background: rgba(255,255,255,0.85);
          padding: 2px 6px;
        ">15 min walk</span>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0]
      });
      // Place label at the top of the circle (1200m north)
      const labelLat = PROPERTY_LAT + 1200 / 111320;
      L.marker([labelLat, PROPERTY_LNG], {
        icon: walkLabelIcon,
        interactive: false
      }).addTo(map);
      // Amenity markers
      const amenityIcon = L.divIcon({
        className: 'amenity-marker',
        html: `
          <div style="
            width: 12px;
            height: 12px;
            background: #000;
            border-radius: 50%;
            border: 2px solid #fff;
            box-shadow: 0 1px 4px rgba(0,0,0,0.25);
          "></div>
        `,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
      AMENITIES.forEach(poi => {
        L.marker([poi.lat, poi.lng], {
          icon: amenityIcon
        }).addTo(map).bindPopup(`<div style="font-family: 'Montserrat', sans-serif; text-align: center; padding: 2px 0;">
              <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #737373;">${poi.name}</span>
              ${poi.distance ? `<br/><span style="font-size: 10px; color: #999;">${poi.distance}</span>` : ''}
            </div>`, {
          closeButton: false
        });
      });
      // Attribution in bottom-left, subtle
      L.control.attribution({
        position: 'bottomleft',
        prefix: false
      }).addAttribution('© <a href="https://www.openstreetmap.org/copyright" style="color:#aaa;">OpenStreetMap</a> · <a href="https://carto.com/" style="color:#aaa;">CARTO</a>').addTo(map);
      mapInstanceRef.current = map;
      // Handle resize
      const handleResize = () => map.invalidateSize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, 100);
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  return <section id="location" className="relative bg-white border-y border-black/5" data-id="element-107" data-mp="3w0xiv8xznyih">
      {/* Section header */}
      <div className="px-6 md:px-12 pt-16 md:pt-32 pb-10 md:pb-16" data-id="element-108" data-mp="4s9yav7kofbz">
        <div className="max-w-7xl mx-auto" data-id="element-109" data-mp="3ma2u9xx9smtv">
          <div className="flex items-center gap-4 mb-6 md:mb-8" data-id="element-110" data-mp="3m889cjm00ups">
            <div className="w-8 h-[1px] bg-black" data-id="element-111" data-mp="3r22zo5w407xu"></div>
            <span className="font-body text-[10px] text-black tracking-widest uppercase" data-id="element-112" data-mp="3d9mxt8ey2fyi">
              The Location
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-6" data-id="element-113" data-mp="3i9ix4ucp5j7q">
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1.1]" data-id="element-114" data-mp="33c0e88mpa087">
              Hawthorn East's <br data-id="element-115" data-mp="38a1y2vrya3kq" />
              <span className="italic text-gray-muted" data-id="element-116" data-mp="38co60wir6l3h">Finest Estate.</span>
            </h2>
            <div className="flex flex-wrap gap-2" data-id="element-117" data-mp="2oqukh3ghafpc">
              {AMENITIES.map(poi => <span key={poi.name} className="font-body text-[9px] md:text-[10px] tracking-loose md:tracking-widest uppercase text-gray-muted border border-black/10 px-2.5 md:px-3 py-1.5 whitespace-nowrap" data-id="element-118" data-mp="2kozkc2aaa8d5">
                  {poi.distance ? `${poi.name} · ${poi.distance}` : poi.name}
                </span>)}
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div ref={mapRef} className="w-full h-[58svh] min-h-[360px] md:h-[65vh] md:min-h-[500px]" data-id="element-119" data-mp="3hdp3dvxnd70p" />

      {/* Leaflet overrides */}
      <style dangerouslySetInnerHTML={{
      __html: `
        .leaflet-container {
          font-family: 'Montserrat', sans-serif;
        }
        .leaflet-control-zoom {
          border: 1px solid rgba(0,0,0,0.1) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }
        .leaflet-control-zoom a {
          color: #000 !important;
          background: #fff !important;
          border-radius: 0 !important;
          border-bottom: 1px solid rgba(0,0,0,0.1) !important;
          width: 44px !important;
          height: 44px !important;
          line-height: 44px !important;
          font-size: 18px !important;
        }
        .leaflet-control-zoom a:last-child {
          border-bottom: none !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 0 !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12) !important;
          padding: 8px 12px !important;
        }
        .leaflet-popup-tip {
          box-shadow: none !important;
        }
        .leaflet-control-attribution {
          font-size: 9px !important;
          background: transparent !important;
          color: #ccc !important;
        }
        .leaflet-control-attribution a {
          color: #aaa !important;
        }
      `
    }} data-id="element-120" data-mp="3cgfgamxqd7g1" />
    </section>;
}